import { type ContextType, useCallback, useEffect, useRef } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { setRemixImageMediaId } from '../../editor-commands/set-remix-image-media-id';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { resolveRemixImageElement } from '../utils/remix-image-node';

// Sourced from the editor-view context type so this file doesn't import the prosemirror EditorView
// re-export directly.
type EditorView = NonNullable<ContextType<typeof ReactEditorViewContext>['editorView']>;

/**
 * Owns the image-specific ready-signal logic (media-node scroll target). Called unconditionally; for
 * non-image remixes it returns inert values so callers fall back to the shared anchor path.
 *
 * Images differ from MAUI apps and whiteboards: the file is uploaded and inserted *after* the remix
 * reports ready, so the thinking-widget anchor no longer marks the result — the media node does.
 */
export const useRemixImageReadySignal = ({
	api,
	isThinking,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	isThinking: boolean;
}): {
	remixImageMediaId: string | undefined;
	resolveImageScrollTarget: (editorView: EditorView) => HTMLElement | null;
} => {
	// Media id of the image this remix inserted (set by the rovo bridge once the upload lands).
	const recordedImageMediaId = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixImageMediaId,
	);

	// Single gate for the whole image ready-signal: everything downstream — the ready bar's viewport
	// measurement, its "View" scroll target and its auto-dismiss window — derives from this id, so no
	// consumer needs its own check and the flow is inert when the experiment is off.
	// No-exposure: this runs for every remix, so exposing here would count users who never produce an
	// image. The behavioural fork is exposed in the rovo bridge.
	const remixImageMediaId = expValEqualsNoExposure(
		'cc_maui_polish_changes_batch_4',
		'isEnabled',
		true,
	)
		? recordedImageMediaId
		: undefined;

	// Clear an image id left over from a previous remix when a new one starts (rising edge of
	// isThinking), so a later non-image remix never targets a stale image.
	const prevIsThinkingRef = useRef(false);
	useEffect(() => {
		const justStartedThinking = isThinking && !prevIsThinkingRef.current;
		prevIsThinkingRef.current = isThinking;
		if (justStartedThinking && remixImageMediaId !== undefined) {
			api?.core.actions.execute(setRemixImageMediaId({ mediaId: undefined }));
		}
	}, [isThinking, remixImageMediaId, api]);

	// The inserted image's DOM element. Null for non-image remixes or before the upload lands —
	// callers fall back to the anchor resolution.
	const resolveImageScrollTarget = useCallback(
		(editorView: EditorView): HTMLElement | null => {
			if (remixImageMediaId === undefined) {
				return null;
			}
			return resolveRemixImageElement(editorView, remixImageMediaId);
		},
		[remixImageMediaId],
	);

	return { remixImageMediaId, resolveImageScrollTarget };
};
